import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'

// Theme-aware system status-bar icons (F-001-T4e, Item C).
//
// On Android the top system status-bar icons (clock/wifi/battery) render light
// by default and are unreadable on the app's light backgrounds. Make them match
// the visible background so they stay readable everywhere:
//   - dark background  -> LIGHT icons -> Style.Dark
//   - light background -> DARK icons  -> Style.Light
//
// NOTE on the @capacitor/status-bar enum: Style.Dark = "light text for dark
// backgrounds" (light icons) and Style.Light = "dark text for light
// backgrounds" (dark icons) — so a dark background maps to Style.Dark and a
// light background maps to Style.Light.
//
// Android-only. iOS already renders the status bar correctly per the device
// screenshots and the spec directs us NOT to regress it, so this is gated to
// android specifically (not just native): on web AND iOS it is a no-op, so the
// StatusBar plugin is never driven off this path there. Fully guarded and
// fire-and-forget: a throw or promise rejection must never break theming.
function setStatusBarStyle(style) {
  if (!Capacitor.isNativePlatform()) return
  // iOS is correct already — only Android needs the theme-aware override.
  if (Capacitor.getPlatform() !== 'android') return
  try {
    StatusBar.setStyle({ style }).catch(() => {})
  } catch {
    // StatusBar plugin unavailable / not registered — ignore.
  }
}

// Golf-arm screens honor the active golf theme's background:
//   - classic (dark theme)       -> LIGHT icons -> Style.Dark
//   - light / bold (light theme) -> DARK icons  -> Style.Light
export function applyStatusBarForTheme(theme) {
  setStatusBarStyle(theme === 'classic' ? Style.Dark : Style.Light)
}

// Shell screens (login / signup) paint a FIXED dark Matte Navy background via
// [data-shell="tournyflex"] regardless of the active golf theme, so their icons
// must always be LIGHT — driving them off the golf theme would put dark icons
// on the dark shell (dark-on-dark, unreadable) on the default 'light' theme.
export function applyStatusBarForShell() {
  setStatusBarStyle(Style.Dark)
}

// Hook for shell pages: force LIGHT icons while the shell screen is mounted, and
// restore the golf-theme-driven icons on unmount (e.g. login -> home) or when
// the theme changes underneath the shell. `theme` comes from useTheme() in the
// page so this stays free of a circular import with ThemeContext.
export function useShellStatusBar(theme) {
  useEffect(() => {
    applyStatusBarForShell()
    return () => applyStatusBarForTheme(theme)
  }, [theme])
}
